import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeleteTicket } from "../../hooks/useTickets";
import { showToast } from "../../lib/toast";
import { getErrorMessage } from "../../lib/apiErrors";

interface TicketTrashDialogProps {
  ticketId: string;
  ticketNumber: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}

/**
 * Confirms moving a ticket to the trash (admins). It's restorable from Organization Settings → Deleted Tickets until
 * it's purged 30 days later.
 */
export const TicketTrashDialog: React.FC<TicketTrashDialogProps> = ({
  ticketId,
  ticketNumber,
  open,
  onOpenChange,
  onDeleted,
}) => {
  const deleteTicketMutation = useDeleteTicket();

  const handleDelete = async () => {
    try {
      await deleteTicketMutation.mutateAsync(ticketId);
      showToast.success("Ticket Deleted", `Ticket #${ticketNumber} was moved to the trash.`);
      onOpenChange(false);
      onDeleted();
    } catch (error) {
      showToast.error("Delete Failed", getErrorMessage(error, "Failed to delete ticket. Please try again."));
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete ticket #{ticketNumber}?</AlertDialogTitle>
          <AlertDialogDescription>
            The ticket moves to the trash and disappears from ticket lists, reports and the customer portal. An admin
            can restore it from Organization Settings → Deleted Tickets for 30 days; after that it's permanently
            deleted with its comments, history and attachments.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleDelete();
            }}
            disabled={deleteTicketMutation.isPending}
            className="bg-red-600 hover:bg-red-700"
          >
            {deleteTicketMutation.isPending ? "Deleting..." : "Delete Ticket"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
